// The next island, built before the posse gets there. Islands are swapped, not streamed (sim/run.ts),
// and every island is a pure function of (run seed, index), so the islands the posse can warp to next
// are known the moment it lands. They're built in frame-budget slices while the posse fights, their
// shaders compiled and buffers uploaded hidden (client/render/warm.ts), and the warp just swaps them in.

import * as THREE from 'three';
import { World, ISLANDS_PER_VILLAGE, BONUS_INDEX, IslandKind, hasBonusDoor } from '../../../sim/world';
import { WorldView } from './index';
import { budget, warmGroup } from '../warm';
import { HAZE } from '../materials';
import { ExtractView } from '../run/extract';

/** The islands a warp can land on from `index` (the likeliest first). */
export function nextIslands(index: number): number[] {
  if (index >= BONUS_INDEX) return [(index - BONUS_INDEX) * ISLANDS_PER_VILLAGE + ISLANDS_PER_VILLAGE - 1]; // bonus exit: the boss
  const out = [index + 1];
  if (hasBonusDoor(index)) out.push(BONUS_INDEX + Math.floor(index / ISLANDS_PER_VILLAGE));
  return out;
}

interface Item { world: World; view: WorldView; extract: ExtractView | null; warm: Generator<unknown, void, unknown> | null; ready: boolean }

export class IslandPrefetch {
  private items: Item[] = [];
  constructor(private scene: THREE.Scene, private camera: THREE.PerspectiveCamera, private renderer: THREE.WebGLRenderer) {}

  private quality = 2;
  /** Keep exactly these islands built or building (drops the rest). */
  want(seed: number, indices: number[], quality: number) {
    this.quality = quality;
    for (const it of [...this.items]) if (it.world.runSeed !== seed || !indices.includes(it.world.index)) this.drop(it);
    for (const index of indices) {
      if (this.items.some((it) => it.world.index === index)) continue;
      const world = new World(seed, index);
      const view = new WorldView({ scene: this.scene, camera: this.camera, renderer: this.renderer, world, quality }, { deferred: true });
      budget.add(view.building);
      this.items.push({ world, view, extract: null, warm: null, ready: false });
    }
  }

  /** Per frame: start warming each island once all its parts have arrived. */
  update() {
    for (const it of this.items) {
      // the boss island's bell and extraction train come with it (hidden until the boss is down)
      if (it.view.built && !it.extract && it.world.kind === IslandKind.Boss) {
        it.extract = new ExtractView({ scene: this.scene, camera: this.camera, renderer: this.renderer, world: it.world, quality: this.quality }, HAZE.uTime);
      }
      if (it.warm || !it.view.loaded || (it.extract && !it.extract.loaded)) continue;
      const warm = warmGroup(this.renderer, this.scene, this.camera, it.view.group);
      const ex = it.extract ? warmGroup(this.renderer, this.scene, this.camera, it.extract.group) : null;
      it.warm = (function* () { yield* warm; if (ex) yield* ex; it.ready = true; })();
      budget.add(it.warm);
    }
  }

  /** The session's World for this island, if it's been made already (same seed, same index). */
  world(seed: number, index: number): World | null {
    return this.items.find((it) => it.world.runSeed === seed && it.world.index === index)?.world ?? null;
  }

  /** Hand over the view built for `world` (finished on the spot if the posse got there first). */
  take(world: World): { view: WorldView; extract: ExtractView | null; ready: boolean } | null {
    const it = this.items.find((x) => x.world === world);
    if (!it) return null;
    this.items.splice(this.items.indexOf(it), 1);
    if (it.warm) budget.drop(it.warm);
    return { view: it.view, extract: it.extract, ready: it.ready };
  }

  get status() { return this.items.map((it) => `${it.world.index}:${it.ready ? 'ready' : it.view.loaded ? 'warming' : it.view.built ? 'loading' : 'building'}`).join(' '); }

  clear() { for (const it of [...this.items]) this.drop(it); }

  private drop(it: Item) {
    budget.drop(it.view.building);
    if (it.warm) budget.drop(it.warm);
    it.view.dispose();
    it.extract?.dispose();
    this.items.splice(this.items.indexOf(it), 1);
  }
}
